// apps/mtd-itsa-web/src/pages/dev-fraud.tsx
import { useState, useEffect } from 'react';
import { useFraudContext } from '../hooks/use-fraud-context';
import { Button } from '@/components/base/buttons/button';
import { useHandleError } from '../hooks/use-handle-error';
import { useAuth } from '@/providers/auth-provider';
import { useHmrcApi } from '../hooks/use-hmrc-api';

export default function DevFraud() {
    const fraudContext = useFraudContext();
    const [result, setResult] = useState<any>(null);
    const api = useHmrcApi();
    const [loading, setLoading] = useState(false);
    const [debugHeaders, setDebugHeaders] = useState<any>(null);
    const [debugLoading, setDebugLoading] = useState(false);
    const [copied, setCopied] = useState<string | null>(null);
    const handleError = useHandleError();
    const { user } = useAuth();
    const [helloLoading, setHelloLoading] = useState(false);
    const [helloResult, setHelloResult] = useState<any>(null);
    const [testScenario, setTestScenario] = useState('');
    const [savedScenario, setSavedScenario] = useState('');

    // Load current test scenario from localStorage on mount
    useEffect(() => {
        const current = localStorage.getItem('hmrc-test-scenario') || '';
        setTestScenario(current);
        setSavedScenario(current);
    }, []);

    // Update localStorage when scenario changes
    const handleScenarioChange = (value: string) => {
        if (value) {
            localStorage.setItem('hmrc-test-scenario', value);
        } else {
            localStorage.removeItem('hmrc-test-scenario');
        }
        setSavedScenario(value); // Update the saved display
    };


    const runTest = async () => {
        setLoading(true);
        setResult(null);
        setDebugHeaders(null);
        setHelloResult(null);
        setCopied(null);

        try {
            const res = await api.post(
                `${import.meta.env.VITE_API_URL}/api/hmrc/test-fraud-headers`,
                { fraudContext }
            );

            const json = await res.json();
            console.log("HMRC fraud header validation:", json);
            setResult(json);
        } catch (error) {
            console.error(error);
            setResult({ error: 'Failed to fetch' });
        } finally {
            setLoading(false);
        }
    };

    const runHelloUserTest = async () => {
        setHelloLoading(true);
        setHelloResult(null);
        setResult(null);
        setDebugHeaders(null);
        setCopied(null);

        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/hmrc/hello-world/user`, {
                method: "GET",
                headers: {
                    "x-user-uuid": user?.id ?? "",
                    "x-fraud-context": JSON.stringify(fraudContext),
                },
                credentials: "include",
            });

            const json = await res.json().catch(() => ({}));
            setHelloResult(res.ok ? json : { status: res.status, ...json });
        } catch (error) {
            console.error(error);
            setHelloResult({ error: 'Failed to fetch' });
        } finally {
            setHelloLoading(false);
        }
    };

    const triggerHandledError = () => {
        handleError(new Error('DevFraud handled error test: ' + new Date().toISOString()), 'DevFraud');
    };

    const triggerFrontendError = () => {
        throw new Error("Sentry Frontend Test Error: " + new Date().toISOString());
    };

    const [shouldCrash, setShouldCrash] = useState(false);

    const triggerErrorBoundary = () => {
        setShouldCrash(true);
    };



    const getDebugHeaders = async () => {
        setDebugLoading(true);
        setDebugHeaders(null);
        setResult(null);
        setHelloResult(null);
        setCopied(null);

        try {
            // [4] Refactored to use api.post
            const res = await api.post(
                `${import.meta.env.VITE_API_URL}/api/hmrc/debug-fraud-headers`,
                { fraudContext }
            );

            const json = await res.json();
            console.log("Debug fraud headers:", json);
            setDebugHeaders(json);
        } catch (error) {
            console.error(error);
            setDebugHeaders({ error: 'Failed to fetch' });
        } finally {
            setDebugLoading(false);
        }
    };

    const handleCopy = (data: any, type: string) => {
        if (!data) return;
        const text = JSON.stringify(data, null, 2);
        navigator.clipboard.writeText(text).then(() => {
            setCopied(type);
            setTimeout(() => setCopied(null), 2000);
        });
    };

    if (shouldCrash) {
        throw new Error("Error Boundary Test: " + new Date().toISOString());
    }

    return (
        <div className="flex flex-col gap-8 max-w-3xl mx-auto py-10">
            <div>
                <h1 className="text-display-sm font-semibold text-primary md:text-display-md">
                    Dev Fraud Test
                </h1>
                <p className="mt-2 text-sm text-tertiary">
                    Test HMRC Fraud Headers generation
                </p>
            </div>

            {/* Test Scenario Input Section */}
            <div className="rounded-lg border border-secondary p-4 bg-gray-50">
                <h3 className="text-sm font-medium text-primary mb-3">Gov-Test-Scenario Header</h3>

                <div className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={testScenario}
                        onChange={(e) => setTestScenario(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleScenarioChange(testScenario);
                            }
                        }}
                        placeholder="e.g., NOT_FOUND, INVALID_NINO, STATEFUL"
                        className="flex-1 px-3 py-2 text-sm border border-secondary rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <Button
                        onClick={() => handleScenarioChange(testScenario)}
                        size="sm"
                    >
                        Set
                    </Button>
                    <Button
                        onClick={() => {
                            setTestScenario('');
                            handleScenarioChange('');
                        }}
                        color="secondary"
                        size="sm"
                    >
                        Clear
                    </Button>
                </div>

                <p className="text-xs text-tertiary">
                    Current: <code className="bg-white px-2 py-1 rounded border border-secondary">
                        {savedScenario || '(none)'}
                    </code>
                    {' '}· Press Enter or click "Set" to save · Changes take effect immediately
                </p>

            </div>

            <div className="w-full max-w-md space-y-6">
                <div className="flex flex-col gap-3">
                    <Button
                        onClick={triggerHandledError}
                        color="secondary"
                        size="md"
                        className="w-full"
                        isDisabled={loading || debugLoading || helloLoading}
                    >
                        Trigger handled error (window.alert)
                    </Button>

                    <Button
                        onClick={runTest}
                        color="primary"
                        size="md"
                        className="w-full"
                        isDisabled={loading || debugLoading || helloLoading}
                    >
                        {loading ? 'Testing...' : 'Test HMRC Fraud Headers'}
                    </Button>

                    {/* NEW BUTTON */}
                    <Button
                        onClick={runHelloUserTest}
                        color="primary"
                        size="md"
                        className="w-full"
                        isDisabled={loading || debugLoading || helloLoading}
                    >
                        {helloLoading ? 'Calling...' : 'Test Hello World (User)'}
                    </Button>

                    <Button
                        onClick={getDebugHeaders}
                        color="secondary"
                        size="md"
                        className="w-full"
                        isDisabled={loading || debugLoading || helloLoading}
                    >
                        {debugLoading ? 'Getting Headers...' : 'Get Debug Headers'}
                    </Button>

                    <Button
                        onClick={triggerFrontendError}
                        color="secondary"
                        size="md"
                        className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white"
                    >
                        Trigger Sentry Error
                    </Button>
                    <Button
                        onClick={triggerErrorBoundary}
                        color="secondary"
                        size="md"
                        className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    >
                        Trigger Error Boundary
                    </Button>
                </div>

                {helloResult && (
                    <div className="rounded-lg border border-secondary p-4 overflow-hidden">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-primary">Hello World (User) Result:</h3>
                            <Button
                                onClick={() => handleCopy(helloResult, 'hello')}
                                color="secondary"
                                size="sm"
                            >
                                {copied === 'hello' ? 'Copied!' : 'Copy'}
                            </Button>
                        </div>
                        <pre className="overflow-auto text-xs text-primary max-h-96 whitespace-pre-wrap break-all">
                            {JSON.stringify(helloResult, null, 2)}
                        </pre>
                    </div>
                )}

                {result && (
                    <div className="rounded-lg border border-secondary p-4 overflow-hidden">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-primary">Validation Result:</h3>
                            <Button
                                onClick={() => handleCopy(result, 'result')}
                                color="secondary"
                                size="sm"
                            >
                                {copied === 'result' ? 'Copied!' : 'Copy'}
                            </Button>
                        </div>
                        <pre className="overflow-auto text-xs text-primary max-h-96 whitespace-pre-wrap break-all">
                            {JSON.stringify(result, null, 2)}
                        </pre>
                    </div>
                )}

                {debugHeaders && (
                    <div className="rounded-lg border border-secondary p-4 overflow-hidden">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-medium text-primary">Debug Headers:</h3>
                            <Button
                                onClick={() => handleCopy(debugHeaders, 'debug')}
                                color="secondary"
                                size="sm"
                            >
                                {copied === 'debug' ? 'Copied!' : 'Copy'}
                            </Button>
                        </div>
                        <pre className="overflow-auto text-xs text-primary max-h-96 whitespace-pre-wrap break-all">
                            {JSON.stringify(debugHeaders, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
}